import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Monitor, MapPin, CheckCircle, Settings } from "lucide-react";

// Kyle Rise Kiosk Configuration
const KIOSK_OPTIONS = [
  {
    kiosk_id: "kiosk_001",
    name: "Foot of Ramp",
    location: "Entry Kiosk",
    reader: "Entry Terminal (tmr_GkHbEAsPErS8tQ)"
  },
  {
    kiosk_id: "kiosk_002",
    name: "Undercroft",
    location: "Exit Kiosk", 
    reader: "Exit Terminal (tmr_GkHaWg45xjvG3D)"
  }
];

const STORAGE_KEY = "parkwise_kiosk_id";

export function getStoredKioskId() {
  return localStorage.getItem(STORAGE_KEY);
}

export function setStoredKioskId(kioskId) {
  localStorage.setItem(STORAGE_KEY, kioskId);
}

export function clearStoredKioskId() {
  localStorage.removeItem(STORAGE_KEY);
}

export default function KioskSetup() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const handleSelect = (kioskId) => {
    setSelected(kioskId);
  };

  const handleConfirm = () => {
    if (!selected) return;
    setStoredKioskId(selected);
    navigate("/kiosk/attract");
  };

  const selectedKiosk = KIOSK_OPTIONS.find(k => k.kiosk_id === selected);

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px"
    }}>
      <div style={{
        background: "white",
        borderRadius: "24px",
        padding: "48px",
        maxWidth: "600px",
        width: "100%",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
      }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <Settings size={48} color="#1e3a5f" style={{ marginBottom: "16px" }} />
          <h1 style={{ 
            fontSize: "28px", 
            fontWeight: "700", 
            color: "#1e3a5f",
            margin: "0 0 8px 0"
          }}>
            Kiosk Setup
          </h1>
          <p style={{ color: "#64748b", margin: 0 }}>
            Select this device&apos;s identity. This is a one-time configuration.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "32px" }}>
          {KIOSK_OPTIONS.map((kiosk) => (
            <button
              key={kiosk.kiosk_id}
              onClick={() => handleSelect(kiosk.kiosk_id)}
              data-testid={`kiosk-option-${kiosk.kiosk_id}`}
              style={{
                padding: "24px",
                borderRadius: "16px",
                border: selected === kiosk.kiosk_id 
                  ? "3px solid #3b82f6" 
                  : "2px solid #e2e8f0",
                background: selected === kiosk.kiosk_id 
                  ? "#eff6ff" 
                  : "white",
                cursor: "pointer",
                textAlign: "left",
                transition: "all 0.2s ease"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "12px",
                  background: selected === kiosk.kiosk_id ? "#3b82f6" : "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <Monitor size={28} color={selected === kiosk.kiosk_id ? "white" : "#64748b"} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ 
                    margin: "0 0 4px 0", 
                    fontSize: "20px", 
                    fontWeight: "600",
                    color: "#1e293b"
                  }}>
                    {kiosk.name}
                  </h3>
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "6px",
                    color: "#64748b",
                    fontSize: "14px"
                  }}>
                    <MapPin size={14} />
                    {kiosk.location}
                  </div>
                  <p style={{ 
                    margin: "8px 0 0 0", 
                    fontSize: "12px", 
                    color: "#94a3b8" 
                  }}>
                    {kiosk.reader}
                  </p>
                </div>
                {selected === kiosk.kiosk_id && (
                  <CheckCircle size={24} color="#3b82f6" />
                )}
              </div>
            </button>
          ))}
        </div>

        {selected && (
          <div style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "24px"
          }}>
            <p style={{ margin: 0, color: "#166534", fontSize: "14px" }}>
              <strong>Confirm:</strong> This device will permanently identify as <strong>{selectedKiosk?.name}</strong> ({selected}).
              All payments will route to <strong>{selectedKiosk?.reader.split(" (")[0]}</strong>.
            </p>
          </div>
        )}

        <button
          onClick={handleConfirm}
          disabled={!selected}
          data-testid="confirm-kiosk-setup"
          style={{
            width: "100%",
            padding: "16px 32px",
            borderRadius: "12px",
            border: "none",
            background: selected ? "#3b82f6" : "#e2e8f0",
            color: selected ? "white" : "#94a3b8",
            fontSize: "18px",
            fontWeight: "600",
            cursor: selected ? "pointer" : "not-allowed"
          }}
        >
          Confirm & Continue
        </button>

        <p style={{ 
          textAlign: "center", 
          marginTop: "24px", 
          fontSize: "12px", 
          color: "#94a3b8" 
        }}>
          This setting is stored locally on this device and will persist across browser restarts.
          To reconfigure, clear browser data or access /kiosk/setup directly.
        </p>
      </div>
    </div>
  );
}
